import React, { useState, useEffect } from 'react';
import { safeTrim } from '../utils/validation';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { CheckCircle, XCircle, Clock, AlertTriangle, User, MapPin, Phone, Package } from 'lucide-react';

const PublicTicketUpdate = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    status: '',
    pending_reason: '',
    notes: ''
  });

  useEffect(() => {
    fetchTicket();
  }, [token]);

  const fetchTicket = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/ticket/${token}`
      );
      
      if (response.data.success) {
        setTicket(response.data.data);
        setFormData(prev => ({
          ...prev,
          status: response.data.data.status,
          notes: response.data.data.notes || ''
        }));
      }
    } catch (err) {
      console.error('Error fetching ticket:', err);
      setError(err.response?.data?.detail || 'Tiket tidak ditemukan atau link sudah tidak valid');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.status === 'PENDING' && !safeTrim(formData.pending_reason)) {
      setError('Catatan wajib diisi untuk status PENDING');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/ticket/${token}/status`,
        {
          status: formData.status,
          pending_reason: safeTrim(formData.pending_reason) || null,
          notes: safeTrim(formData.notes) || null
        }
      );

      if (response.data.success) {
        setSuccess(true);
      }
    } catch (err) {
      console.error('Error updating ticket:', err);
      setError(err.response?.data?.detail || 'Gagal update status tiket');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'OPEN': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'OPEN' },
      'PENDING': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'PENDING' },
      'CLOSE': { bg: 'bg-green-100', text: 'text-green-800', label: 'CLOSE' }
    };
    
    const badge = badges[status] || badges['OPEN'];
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getTypeIcon = (type) => {
    const icons = {
      'PEMASANGAN': '🔧',
      'PROBLEM': '⚠️',
      'REQUEST': '📝'
    };
    return icons[type] || '🎫';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-slate-300">Memuat data tiket...</p>
        </div>
      </div>
    );
  }

  if (error && !ticket) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center">
              <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Tiket Tidak Ditemukan</h2>
              <p className="text-gray-600 dark:text-slate-300">{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Update Berhasil!</h2>
              <p className="text-gray-600 mb-4 dark:text-slate-300">Status tiket telah berhasil diperbarui.</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800 dark:bg-blue-900/30">
                Link ini sudah tidak dapat digunakan lagi. Anda akan menerima link baru jika ada update lebih lanjut.
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 py-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-2 dark:text-slate-200">Update Status Tiket</h1>
          <p className="text-gray-600 dark:text-slate-300">ISP Billing System</p>
        </div>

        {/* Ticket Info */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{getTypeIcon(ticket.ticket_type)}</span>
                <div>
                  <CardTitle className="text-lg">{ticket.ticket_type}</CardTitle>
                  <CardDescription>#{ticket.ticket_number}</CardDescription>
                </div>
              </div>
              {getStatusBadge(ticket.status)}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-2">
              <User className="h-4 w-4 text-gray-500 mt-0.5 dark:text-slate-400" />
              <div>
                <p className="text-sm text-gray-500 dark:text-slate-400">Customer</p>
                <p className="font-medium">{ticket.customer_name}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 text-gray-500 mt-0.5 dark:text-slate-400" />
              <div>
                <p className="text-sm text-gray-500 dark:text-slate-400">Alamat</p>
                <p className="font-medium">{ticket.customer_address}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <Phone className="h-4 w-4 text-gray-500 mt-0.5 dark:text-slate-400" />
              <div>
                <p className="text-sm text-gray-500 dark:text-slate-400">No. HP</p>
                <p className="font-medium">{ticket.customer_phone}</p>
              </div>
            </div>
            
            {ticket.service_package && (
              <div className="flex items-start gap-2">
                <Package className="h-4 w-4 text-gray-500 mt-0.5 dark:text-slate-400" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-slate-400">Paket</p>
                  <p className="font-medium">{ticket.service_package}</p>
                </div>
              </div>
            )}

            {ticket.pppoe_username && (
              <div className="bg-gray-50 rounded-lg p-3 mt-3 dark:bg-slate-900">
                <p className="text-sm text-gray-500 mb-1 dark:text-slate-400">PPPoE Credentials</p>
                <p className="text-sm"><strong>User:</strong> {ticket.pppoe_username}</p>
                <p className="text-sm"><strong>Pass:</strong> {ticket.pppoe_password}</p>
              </div>
            )}

            {ticket.notes && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3 dark:bg-blue-900/30">
                <p className="text-sm text-blue-800"><strong>Catatan:</strong> {ticket.notes}</p>
              </div>
            )}

            <div className="flex items-center gap-2 text-sm text-gray-500 pt-2 dark:text-slate-400">
              <Clock className="h-4 w-4" />
              <span>Dibuat: {new Date(ticket.created_at).toLocaleString('id-ID')}</span>
            </div>
          </CardContent>
        </Card>

        {/* Update Form */}
        <Card>
          <CardHeader>
            <CardTitle>Update Status</CardTitle>
            <CardDescription>Perbarui status pengerjaan tiket ini</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert className="bg-red-50 text-red-800 border-red-200 dark:bg-red-900/30">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="status">Status Tiket</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => {
                    setFormData(prev => ({ ...prev, status: value }));
                    setError('');
                  }}
                >
                  <SelectTrigger id="status">
                    <SelectValue placeholder="Pilih status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OPEN">OPEN - Masih dikerjakan</SelectItem>
                    <SelectItem value="PENDING">PENDING - Ada kendala</SelectItem>
                    <SelectItem value="CLOSE">CLOSE - Selesai</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.status === 'PENDING' && (
                <div className="space-y-2">
                  <Label htmlFor="pending_reason">Catatan Kendala *</Label>
                  <Textarea
                    id="pending_reason"
                    placeholder="Jelaskan kendala yang dihadapi..."
                    value={formData.pending_reason}
                    onChange={(e) => setFormData(prev => ({ ...prev, pending_reason: e.target.value }))}
                    rows={3}
                    className="resize-none"
                  />
                  <p className="text-sm text-gray-500 dark:text-slate-400">Wajib diisi jika status PENDING</p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="notes">Catatan Tambahan (Opsional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Tambahkan catatan jika diperlukan..."
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="resize-none"
                />
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 dark:bg-yellow-900/30">
                <p className="text-sm text-yellow-800">
                  <strong>Perhatian:</strong> Setelah submit, link ini tidak dapat digunakan lagi.
                </p>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                disabled={submitting || !formData.status}
              >
                {submitting ? 'Menyimpan...' : 'Update Status Tiket'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-gray-500 dark:text-slate-400">
          <p>© 2025 ISP Billing System</p>
        </div>
      </div>
    </div>
  );
};

export default PublicTicketUpdate;
